import { isDisplayP3Supported } from './p3.js';

/**
 * Returns a CSS color string for the fully-saturated hue `h` (in [0, 1]), formatted for the
 * given color space. A hue at full saturation and value is the pure spectral color, i.e.
 * HSV(h, 1, 1). A `null` hue is treated as 0 (red).
 *
 * @see {@link rgbaToCss} for how the color space is formatted
 */
function hueToCss(h, colorSpace = 'srgb') {
    const { r, g, b } = hsvToRgbNormalized(h ?? 0, 1, 1);
    return rgbaToCss(r, g, b, 1, colorSpace);
}
/**
 * Format RGBA values as a CSS color string for the given color space.
 * All channel values are in [0, 1].
 *
 * For `'srgb'` (or when CSS Color Level 4 is not supported): returns `rgba(R, G, B, A)` with 0–255 r/g/b.
 * For `'display-p3'`: returns `color(display-p3 r g b / a)` with 0–1 values.
 */
function rgbaToCss(r, g, b, a, colorSpace) {
    return colorSpace === 'display-p3' && isDisplayP3Supported()
        ? `color(display-p3 ${r} ${g} ${b} / ${a})`
        : `rgb(${norm255(r)} ${norm255(g)} ${norm255(b)} / ${a})`;
}
/**
 * Returns a CSS `rgb(r g b)` string from HSL inputs (all in [0, 1]).
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/rgb | MDN: rgb()}
 */
function hslToCss(h, s, l) {
    const { r, g, b } = hslToRgb(h, s, l);
    return `rgb(${r} ${g} ${b})`;
}
/**
 * Returns a CSS `rgb(r g b)` string from HSV inputs (all in [0, 1]).
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/rgb | MDN: rgb()}
 */
function hsvToCss(h, s, v) {
    const { r, g, b } = hsvToRgb(h, s, v);
    return `rgb(${r} ${g} ${b})`;
}
/**
 * Convert HSV to RGB with channel values in [0, 1].
 *
 * @see {@link hsvToRgb} for [0, 255] output
 */
function hsvToRgbNormalized(h, s, v) {
    const { r, g, b } = hsvToRgb(h, s, v);
    return { r: r / 255, g: g / 255, b: b / 255 };
}
/**
 * Convert HSV to RGB.
 * All inputs are in [0, 1]. Returns { r, g, b } with values in [0, 255].
 *
 * @see {@link https://en.wikipedia.org/wiki/HSL_and_HSV#HSV_to_RGB | HSV to RGB conversion}
 */
function hsvToRgb(h, s, v) {
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    let r, g, b;
    // prettier-ignore
    switch (i % 6) {
        case 0:
            r = v;
            g = t;
            b = p;
            break;
        case 1:
            r = q;
            g = v;
            b = p;
            break;
        case 2:
            r = p;
            g = v;
            b = t;
            break;
        case 3:
            r = p;
            g = q;
            b = v;
            break;
        case 4:
            r = t;
            g = p;
            b = v;
            break;
        default:
            r = v;
            g = p;
            b = q;
            break;
    }
    return { r: norm255(r), g: norm255(g), b: norm255(b) };
}
/**
 * Convert HSL to RGB.
 * All inputs are in [0, 1]. Returns { r, g, b } with values in [0, 255].
 *
 * @see {@link https://en.wikipedia.org/wiki/HSL_and_HSV#HSL_to_RGB | HSL to RGB conversion}
 */
function hslToRgb(h, s, l) {
    if (s === 0) {
        const v = norm255(l);
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    function hue2rgb(t) {
        if (t < 0)
            t += 1;
        else if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    }
    return {
        r: norm255(hue2rgb(h + 1 / 3)),
        g: norm255(hue2rgb(h)),
        b: norm255(hue2rgb(h - 1 / 3)),
    };
}
function norm255(v) {
    return Math.round(v * 255);
}

export { hslToCss, hslToRgb, hsvToCss, hsvToRgb, hsvToRgbNormalized, hueToCss, rgbaToCss };
//# sourceMappingURL=color.js.map
